import { ArrowRight, ArrowUpRight, TrendingUp } from "lucide-react";
import Eyebrow from "@/components/zeblan/Eyebrow";
import ParticleCanvas from "@/components/zeblan/ParticleCanvas";

export default function Hero({ onOpenLead }) {
  const bars = [38, 46, 52, 60, 68, 80, 92];

  return (
    <section id="top" className="relative min-h-screen bg-navy text-white overflow-hidden">
      {/* radial glow */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[680px] h-[680px] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(54,209,197,0.35), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[760px] h-[760px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.45), transparent 70%)" }}
      />
      <ParticleCanvas />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 lg:pt-40 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <Eyebrow tone="teal">Intelligence in Motion</Eyebrow>
          <h1
            className="mt-6 font-extrabold leading-[1.05] tracking-[-0.02em]"
            style={{ fontSize: "clamp(38px, 6vw, 68px)" }}
            data-testid="hero-title"
          >
            <span className="block">AI solutions, built by people who</span>
            <span className="block font-display italic font-medium text-mist/95" style={{ fontWeight: 500 }}>
              understand business.
            </span>
          </h1>

          <p className="mt-7 max-w-[560px] text-mist/85 font-light text-base md:text-lg leading-relaxed">
            We help mid-sized companies implement secure, custom AI tools that actually make daily work easier.{" "}
            <span className="font-semibold text-white">Not to replace people. To let them scale.</span> Real results,
            and a team that's with you every step of the way.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => onOpenLead("roi-assessment")}
              className="group inline-flex items-center gap-2 bg-blue hover:bg-blue-deep text-white font-semibold px-6 py-3.5 rounded-md transition-all hover:-translate-y-[2px] shadow-[0_12px_30px_-10px_rgba(37,99,235,0.65)]"
              data-testid="hero-cta-roi"
            >
              Get your free AI ROI assessment
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="#services"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-teal text-white/90 hover:text-teal font-semibold px-6 py-3.5 rounded-md transition-colors"
              data-testid="hero-cta-services"
            >
              Explore our services
            </a>
          </div>

          <div className="mt-8 inline-flex items-center gap-3 text-[13px] text-mist/80">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-teal opacity-60 animate-pulse-dot" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal" />
            </span>
            Local hosting available. Your data never leaves your building.
          </div>
        </div>

        {/* Glass dashboard card */}
        <div className="lg:col-span-5">
          <div
            className="relative bg-white/[0.06] backdrop-blur-xl border border-white/15 rounded-2xl p-6 lg:p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]"
            data-testid="hero-dashboard-card"
          >
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold">
              <span>System Intelligence</span>
              <span className="inline-flex items-center gap-2 text-teal">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-teal opacity-60 animate-pulse-dot" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal" />
                </span>
                Live
              </span>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <div className="font-extrabold tracking-[-0.03em] text-white" style={{ fontSize: "clamp(48px,7vw,72px)" }}>
                98.6
                <span className="text-teal">%</span>
              </div>
              <div className="pb-3 text-[13px] text-mist/80">
                Operational efficiency
                <div className="inline-flex items-center gap-1 mt-1 text-teal">
                  <TrendingUp className="h-3.5 w-3.5" /> +12.4% vs last month
                </div>
              </div>
            </div>

            {/* Bar chart */}
            <div className="mt-6 flex items-end gap-2 h-32">
              {bars.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full rounded-t-md"
                    style={{
                      height: `${v}%`,
                      background: `linear-gradient(to top, #2563EB, #36D1C5)`,
                      opacity: 0.85,
                    }}
                  />
                  <span className="text-[10px] text-white/45">W{i + 1}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center justify-between text-[11px] text-white/50">
              <span>7-week trend · auto-tracked</span>
              <ArrowUpRight className="h-4 w-4 text-teal" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
